import React from 'react';
import styled from 'styled-components';
import { getInstoreOrderDateAndTime, getOrderDateAndTime } from 'shared/helpers/orders';
import { OrderRow, OrderRowTextButton, OrderRowButton } from 'components/order-row/order-row';
import { getUniqueOrderImages } from 'components/order-row/get-unique-order-images';
import { OrderThumbnails } from 'components/order-row/order-thumbnails';
import { ConsumerOrder } from '../../account.types';
import { formatDispensaryAddressWithoutZipOrCountry } from './format-dispensary-address';
import { OrderSource } from './order-list-tab-bar';

type OrderListItemProps = {
  canViewStatus: boolean;
  order: ConsumerOrder;
  onViewOrder: () => void;
  onReorder?: () => void;
  reorderLoading?: boolean;
  orderSource?: OrderSource;
  isFromDifferentLocation?: boolean;
};

export function OrderListItem({
  canViewStatus,
  order,
  onViewOrder,
  onReorder,
  reorderLoading,
  orderSource,
  isFromDifferentLocation = false,
}: OrderListItemProps): JSX.Element {
  const isInStore = orderSource === OrderSource.inStore;
  const { deliveryInfo, totalCost, orders: orderItems } = order;
  const isDelivery = deliveryInfo?.deliveryOption;
  const prefix = orderSource ? `${orderSource} - ` : '';
  const orderType = isDelivery ? 'Delivery' : 'Pickup';
  const title = isInStore ? OrderSource.inStore : `${prefix}${orderType}`;
  const itemCount = orderItems.reduce((sum, item) => sum + (item.quantity ?? 0), 0);
  const itemCountString = `${itemCount} item${itemCount === 1 ? '' : 's'}`;
  const parsed = parseFloat(totalCost ?? '0');
  const totalCostString = `$${(Number.isNaN(parsed) ? 0 : parsed).toFixed(2)}`;
  const date = isInStore ? getInstoreOrderDateAndTime(order.createdAt) ?? '' : getOrderDateAndTime(order);
  const dispensaryAddressLine = formatDispensaryAddressWithoutZipOrCountry(order.dispensary?.address);
  const images = getUniqueOrderImages(orderItems);

  const dispensaryName = order.dispensary?.name ?? 'the original location';

  const actions = (
    <>
      {onReorder &&
        !canViewStatus &&
        (isFromDifferentLocation ? (
          <TooltipWrapper>
            <OrderRowButton variant='secondary' disabled>
              Reorder
            </OrderRowButton>
            <TooltipBubble>
              This order was placed at another location, please go to {dispensaryName} to reorder your previous items.
            </TooltipBubble>
          </TooltipWrapper>
        ) : (
          <OrderRowButton variant='secondary' onClick={onReorder} disabled={reorderLoading}>
            {reorderLoading ? 'Reordering...' : 'Reorder'}
          </OrderRowButton>
        ))}
      {canViewStatus ? (
        <OrderRowButton onClick={onViewOrder}>View Order</OrderRowButton>
      ) : (
        <OrderRowTextButton onClick={onViewOrder}>View Receipt</OrderRowTextButton>
      )}
    </>
  );

  return (
    <OrderRow
      title={title}
      date={date}
      address={dispensaryAddressLine || undefined}
      desktopCol2={totalCostString}
      desktopCol3={itemCountString}
      mobileCol2={totalCostString}
      mobileCol3={itemCountString}
      actions={actions}
      thumbnails={images.length > 0 ? <OrderThumbnails images={images} /> : undefined}
    />
  );
}

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;

  &:hover > div {
    opacity: 1;
    visibility: visible;
    display: block;
  }
`;

const TooltipBubble = styled.div`
  opacity: 0;
  visibility: hidden;
  display: none;
  transition: all 0.3s;
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(44, 47, 50, 0.92);
  color: #f5f7fa;
  font-size: 12px;
  line-height: 16px;
  font-weight: normal;
  text-align: left;
  padding: 15px 18px 16px 21px;
  border-radius: 10px;
  white-space: normal;
  width: 239px;
  z-index: 100;

  &::after {
    content: '';
    width: 0;
    height: 0;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-top: 8px solid rgba(44, 47, 50, 0.92);
    position: absolute;
    bottom: -7px;
    right: calc(50% - 8px);
  }
`;
